import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Play, Pause, X, Headphones, Pin } from 'lucide-react';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import { resolveCoverUrl } from '../utils/coverUrl';
import SleepTimerButton from './SleepTimerButton';
import useDraggable from '../hooks/useDraggable';

// Routes where the mini-player MUST stay hidden:
//   - all auth flows (we don't even know who owns the audio yet)
//   - the full-screen Player page (it's already showing controls)
const HIDDEN_PREFIXES = [
  '/login',
  '/admin-login',
  '/writer-login',
  '/forgot-password',
  '/payment/callback',
  '/player/',
];

const formatTime = (t) => {
  if (!Number.isFinite(t) || t < 0) return '0:00';
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

const MiniPlayerBar = () => {
  const { track, isPlaying, currentTime, duration, toggle, clear } =
    useAudioPlayer();
  const location = useLocation();
  const navigate = useNavigate();
  const { pos, transform, dragging, dragHandlers, reset } = useDraggable({
    defaultY: 0,
  });
  const [showResetHint, setShowResetHint] = useState(false);
  const [coverBroken, setCoverBroken] = useState(false);

  if (!track) return null;
  if (HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p))) {
    return null;
  }

  // Tiny safe haptic helper — `navigator.vibrate` is a no-op on iOS but
  // fires a crisp tap on Android and PWAs that opted in to it. We use
  // a 10ms tap for primary actions (toggle/expand), 6ms for utility,
  // 14ms for destructive (close).
  const haptic = (ms = 10) => {
    try { navigator.vibrate?.(ms); } catch (_) { /* swallow */ }
  };

  const noBottomNav = location.pathname.startsWith('/reader/');
  const bottomOffset = noBottomNav
    ? 'env(safe-area-inset-bottom, 0px)'
    : 'calc(64px + env(safe-area-inset-bottom, 0px))';

  const cover = resolveCoverUrl(track.cover);
  const pct = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;

  const expand = () => {
    haptic(8);
    if (track?.id) navigate(`/player/${track.id}`);
  };

  // Edge anchoring: when the user dragged the bar past 60% of screen width,
  // we let it cling to either side. To keep the card's own dimensions
  // stable (the user complained "minimised player loses shape"), we keep
  // the OUTER container at full viewport width regardless of side, and
  // shift the INNER card with `mr-auto` / `ml-auto`. The card itself
  // always stays at `max-w-3xl` so its width never collapses to its
  // content's natural size.
  const outerSideClass = 'left-0 right-0 px-3 sm:px-4';
  const innerAlignClass =
    pos.side === 'left'
      ? 'mr-auto ml-0'
      : pos.side === 'right'
      ? 'ml-auto mr-0'
      : 'mx-auto';

  // Has the user dragged the bar at all? Show a small "reset" pill if so
  // so they can dock it back to the default position.
  const hasMoved = pos.side !== 'center' || (pos.y || 0) !== 0;

  return (
    <div
      className={`fixed z-50 pointer-events-none ${outerSideClass}`}
      style={{
        bottom: bottomOffset,
        transform,
        // While dragging, drop the transition for 1:1 tracking; on release
        // we put it back so the snap-to-edge animates smoothly.
        transition: dragging ? 'none' : 'transform 220ms cubic-bezier(.2,.9,.3,1)',
        touchAction: 'none', // critical so pointermove fires on touch
      }}
      data-testid="mini-player-bar"
    >
      <div
        className={`pointer-events-auto max-w-3xl w-full rounded-2xl bg-slate-950/90 backdrop-blur-2xl border shadow-2xl overflow-hidden duration-200 ${innerAlignClass} ${
          dragging
            ? 'border-amber-400/80 shadow-amber-500/30 transition-[border-color,box-shadow]'
            : 'border-white/10 shadow-black/50 transition-[border-color,box-shadow]'
        }`}
        style={{
          transitionTimingFunction: 'ease-out',
        }}
        onMouseEnter={() => hasMoved && setShowResetHint(true)}
        onMouseLeave={() => setShowResetHint(false)}
      >
        {/* Thin gradient progress bar — Pocket-FM premium feel */}
        <div className="h-1 w-full bg-white/5 relative">
          <div
            className="h-full bg-gradient-to-r from-indigo-400 via-fuchsia-500 to-pink-500 transition-[width] duration-200"
            style={{ width: `${pct}%` }}
            data-testid="mini-player-progress"
          />
          {pct > 0 && pct < 100 && (
            <div
              className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-white shadow-md shadow-fuchsia-500/50 transition-[left] duration-200"
              style={{ left: `calc(${pct}% - 4px)` }}
              aria-hidden="true"
            />
          )}
        </div>

        <div
          className="flex items-center gap-2 sm:gap-3 px-2.5 sm:px-3 py-2.5 min-h-[64px] cursor-pointer active:cursor-grabbing touch-none select-none"
          {...dragHandlers}
          onClick={(e) => {
            if (e.target?.closest?.('[data-drag-ignore]')) return;
            expand();
          }}
          data-testid="mini-player-body"
          role="button"
          aria-label="Open full player"
        >
          <div
            className="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1 text-left group"
            data-testid="mini-player-expand"
          >
            <div className="relative h-11 w-11 sm:h-12 sm:w-12 rounded-xl overflow-hidden bg-slate-800 ring-1 ring-white/15 flex-shrink-0 shadow-lg shadow-black/40">
              {cover && !coverBroken ? (
                <img
                  src={cover}
                  alt={track.title || 'Now playing'}
                  className="h-full w-full object-cover"
                  onError={() => setCoverBroken(true)}
                />
              ) : (
                <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-indigo-600 to-fuchsia-600">
                  <Headphones className="w-5 h-5 text-white" />
                </div>
              )}
              {isPlaying && (
                <span
                  aria-hidden="true"
                  className="absolute inset-0 ring-2 ring-fuchsia-400/70 rounded-xl pointer-events-none animate-pulse"
                />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p
                className="text-sm font-bold text-white truncate group-hover:text-indigo-200 transition-colors leading-tight"
                data-testid="mini-player-title"
              >
                {track.title || 'Untitled'}
              </p>
              <p className="text-[10.5px] text-white/55 truncate leading-tight mt-0.5">
                <span className="font-mono tabular-nums">
                  {formatTime(currentTime)}
                  {duration > 0 ? ` / ${formatTime(duration)}` : ''}
                </span>
                {track.series_title ? <span> · {track.series_title}</span> : null}
              </p>
            </div>
          </div>

          <div className="flex-shrink-0" data-drag-ignore="true">
            <SleepTimerButton variant="compact" />
          </div>

          <div className="w-9 flex-shrink-0 flex items-center justify-center" data-drag-ignore="true">
            {hasMoved && (
              <button
                type="button"
                onClick={() => { haptic(6); reset(); }}
                className={`h-9 w-9 rounded-full text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 flex items-center justify-center transition-opacity ${
                  showResetHint ? 'opacity-100' : 'opacity-50'
                }`}
                data-testid="mini-player-redock"
                aria-label="Dock back to bottom"
                title="Dock to bottom"
              >
                <Pin className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Play / pause — beefier than before, gradient + breathing ring */}
          <button
            type="button"
            onClick={() => { haptic(12); toggle(); }}
            className="relative h-12 w-12 rounded-full flex items-center justify-center text-white shadow-lg shadow-indigo-900/50 transition-transform flex-shrink-0 active:scale-95"
            style={{
              background: 'radial-gradient(120% 120% at 30% 30%, #818cf8 0%, #6366f1 45%, #4338ca 100%)',
            }}
            data-testid="mini-player-toggle"
            data-drag-ignore="true"
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying && (
              <span
                aria-hidden="true"
                className="absolute inset-0 rounded-full animate-ping opacity-25"
                style={{ background: 'rgba(129,140,248,0.7)' }}
              />
            )}
            {isPlaying ? (
              <Pause className="w-5 h-5" strokeWidth={2.5} fill="currentColor" />
            ) : (
              <Play className="w-5 h-5 ml-0.5" strokeWidth={2.5} fill="currentColor" />
            )}
          </button>

          <button
            type="button"
            onClick={() => { haptic(14); clear(); }}
            className="h-9 w-9 rounded-full text-white/55 hover:text-white hover:bg-white/10 flex items-center justify-center transition-colors flex-shrink-0"
            data-testid="mini-player-close"
            data-drag-ignore="true"
            aria-label="Close mini player"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default MiniPlayerBar;
